import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getReportById, StoredReportData } from '../../services/reportStorage';
import { generateTasPDFReport } from './TasReportGenerator';
import { logReportVisit } from '../../services/reportAnalytics';
import { useVersion } from '../../contexts/VersionContext';

// Component to load and display stored TAS reports using the TAS ReportGenerator
const TasReportViewer: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const [reportData, setReportData] = useState<StoredReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isRoosterBranded } = useVersion();

  useEffect(() => {
    const loadReport = async () => {
      if (!reportId) {
        setError('No report ID provided');
        setLoading(false);
        return;
      }

      try {
        const data = await getReportById(reportId);
        if (data) {
          setReportData(data);
          
          // Log the visit for analytics
          try {
            const organizationName = data.formData?.organizationName || 'Unknown Organization';
            // sessionId is optional - we may not have it for public reports
            await logReportVisit(reportId, organizationName);
          } catch (analyticsError) {
            // Don't let analytics errors affect the report viewing
            console.error('Failed to log TAS report visit:', analyticsError);
          }
        } else {
          setError('TAS report not found');
        }
      } catch (err) {
        console.error('Error loading TAS report:', err);
        setError('Failed to load TAS report');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [reportId]);

  const generateTasReportFromStoredData = useCallback(async (data: StoredReportData, id: string) => {
    const { formData, results, componentData, enhancedData } = data;
    
    // Call the TAS generatePDFReport with the stored data
    // Pass 'inline' flag to indicate we want to display in current window
    try {
      await generateTasPDFReport(
        formData,
        results,
        () => componentData,  // Provide as getter functions
        isRoosterBranded, // Use context to determine branding
        {
          topPerformerTAS: enhancedData?.topPerformerTAS,
          industryAverageTAS: enhancedData?.industryAverageTAS,
          componentData: componentData,
          currentTAS: results.currentTAS,
          projectedTAS: results.projectedTAS
        },
        '', // sessionId not needed for stored reports
        id,  // Pass the reportId
        true // isInline flag - display in current window, not popup
      );
    } catch (error) {
      console.error('Error generating TAS report from stored data:', error);
      setError('Failed to generate TAS report');
    }
  }, [isRoosterBranded]);

  useEffect(() => {
    if (reportData && reportId) {
      // Once we have the data, use the TAS ReportGenerator to display it
      generateTasReportFromStoredData(reportData, reportId);
    }
  }, [reportData, reportId, generateTasReportFromStoredData]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#0284c7'
      }}>
        Loading TAS report...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: '#dc2626',
        fontSize: '18px'
      }}>
        {error}
      </div>
    );
  }

  // The report will be displayed inline
  return null;
};

export default TasReportViewer;